import { useRef, useState, useEffect } from "react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { PenTool, Eraser, Check } from "lucide-react";

// Digital signature: draw on canvas + typed name. Returns {image, name, credential, signed_at}.
export default function SignaturePad({ value, onSign, defaultName = "", defaultCredential = "" }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasInk = useRef(false);
  const [name, setName] = useState(defaultName);
  const [credential, setCredential] = useState(defaultCredential);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#1f2a24";
  }, []);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return { x: p.clientX - rect.left, y: p.clientY - rect.top };
  };
  const startDraw = (e) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d", { willReadFrequently: true });
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const draw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d", { willReadFrequently: true });
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    hasInk.current = true;
  };
  const endDraw = () => (drawing.current = false);

  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext("2d", { willReadFrequently: true }).clearRect(0, 0, canvas.width, canvas.height);
    hasInk.current = false;
  };

  const sign = () => {
    if (!name.trim()) return;
    const image = hasInk.current ? canvasRef.current.toDataURL("image/png") : null;
    onSign({
      image,
      name: name.trim(),
      credential: credential.trim(),
      signed_at: new Date().toLocaleString(),
    });
  };

  if (value) {
    return (
      <div className="rounded-xl border border-[#DCE5DF] bg-[#F4F6F3] p-4" data-testid="signature-signed">
        <p className="text-[10px] font-bold text-[#4A6B53] uppercase tracking-wider mb-2 flex items-center gap-1.5">
          <Check className="w-3.5 h-3.5" strokeWidth={2} /> Signed
        </p>
        {value.image && (
          <img src={value.image} alt="signature" className="h-14 mb-1" />
        )}
        <p className="text-sm font-semibold text-[#121814]">
          {value.name}{value.credential ? `, ${value.credential}` : ""}
        </p>
        <p className="text-xs text-[#7A8C82]">Signed {value.signed_at}</p>
        <button
          data-testid="signature-clear-btn"
          onClick={() => onSign(null)}
          className="mt-2 text-xs font-semibold text-[#C26D5C] hover:underline"
        >
          Remove signature
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-[#E2DFD8] bg-[#F9F9F6] p-4" data-testid="signature-pad">
      <p className="text-[10px] font-bold text-[#7A8C82] uppercase tracking-wider mb-2 flex items-center gap-1.5">
        <PenTool className="w-3.5 h-3.5" strokeWidth={1.5} /> Digital signature — draw below (optional) & type your name
      </p>
      <div className="relative">
        <canvas
          ref={canvasRef}
          width={520}
          height={110}
          data-testid="signature-canvas"
          className="w-full h-[110px] bg-white rounded-lg border border-dashed border-[#C9C6BE] touch-none cursor-crosshair"
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={endDraw}
          onMouseLeave={endDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={endDraw}
        />
        <button
          type="button"
          onClick={clear}
          data-testid="signature-erase-btn"
          className="absolute top-2 right-2 flex items-center gap-1 text-[11px] text-[#7A8C82] hover:text-[#C26D5C]"
        >
          <Eraser className="w-3.5 h-3.5" strokeWidth={1.5} /> Clear
        </button>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-3">
        <Input data-testid="signature-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Type your full name" className="h-10 bg-white border-[#E2DFD8] text-sm" />
        <Input data-testid="signature-credential-input" value={credential} onChange={(e) => setCredential(e.target.value)} placeholder="Credential (e.g. PP)" className="h-10 bg-white border-[#E2DFD8] text-sm" />
      </div>
      <Button
        data-testid="signature-sign-btn"
        onClick={sign}
        disabled={!name.trim()}
        className="mt-3 h-9 px-5 rounded-full bg-[#4A6B53] hover:bg-[#3D5A45] text-white text-sm"
      >
        <Check className="w-4 h-4 mr-1.5" strokeWidth={2} /> Sign note
      </Button>
    </div>
  );
}
